/**
 * Maps legacy user-facing "Patient" / "Subject" wording to "Participant" (ICH E6(R3)).
 * Use for system labels only — not for user-entered data, API keys, field names, or persisted identifiers.
 */
export function toParticipantUiText(text) {
  if (text == null || typeof text !== "string") {
    return text;
  }
  return text
    .replaceAll("Patient/Subject", "Participant/Participant")
    .replaceAll("Patients/Subjects", "Participants/Participants")
    .replaceAll("Subjects", "Participants")
    .replaceAll("Subject", "Participant")
    .replaceAll("SUBJECTS", "PARTICIPANTS")
    .replaceAll("SUBJECT", "PARTICIPANT")
    .replaceAll("subjects", "participants")
    .replaceAll("subject", "participant")
    .replaceAll("Patients", "Participants")
    .replaceAll("Patient", "Participant")
    .replaceAll("PATIENTS", "PARTICIPANTS")
    .replaceAll("PATIENT", "PARTICIPANT")
    .replaceAll("patients", "participants")
    .replaceAll("patient", "participant");
}

export function mapParticipantExcelCellNote(note) {
  if (note == null) {
    return note;
  }
  if (typeof note === "string") {
    return toParticipantUiText(note);
  }
  if (typeof note === "object" && Array.isArray(note.texts)) {
    return {
      ...note,
      texts: note.texts.map((part) => ({
        ...part,
        text:
          typeof part.text === "string"
            ? toParticipantUiText(part.text)
            : part.text,
      })),
    };
  }
  return note;
}

/** Maps one export cell (plain string or xlsx-js-style `{ v }` object). */
export function mapParticipantExportCell(cell) {
  if (typeof cell === "string") {
    return toParticipantUiText(cell);
  }
  if (
    cell &&
    typeof cell === "object" &&
    !(cell instanceof Date) &&
    typeof cell.v === "string"
  ) {
    return { ...cell, v: toParticipantUiText(cell.v) };
  }
  return cell;
}

/** Applies Participant terminology to export header rows only (not user-entered data). */
export function mapParticipantExportContent(tableContent) {
  return mapParticipantExportHeaderRowContent(tableContent);
}

export const PARTICIPANT_EXPORT_SKIP_SHEET_KEYS = new Set([
  "assessments",
  "countryAssessments",
  "siteAssessments",
  "soe",
  "visits",
  "countryVisits",
  "siteVisits",
  "visitSamplingStrategy",
]);

export function mapParticipantExportHeaderRowContent(tableContent) {
  if (!Array.isArray(tableContent) || tableContent.length === 0) {
    return tableContent;
  }
  return tableContent.map((row, rowIndex) => {
    if (rowIndex !== 0 || !Array.isArray(row)) {
      return row;
    }
    return row.map(mapParticipantExportCell);
  });
}

const INITIAL_RISK_STUDY_SPECIFICS_COLUMN = 1;
const INITIAL_RISK_RISK_SCORE_COLUMN = 2;

/** Maps system IRA columns only; preserves user Study Specifics and risk score values. */
export function mapInitialRiskAssessmentExportContent(tableContent) {
  if (!Array.isArray(tableContent)) {
    return tableContent;
  }
  return tableContent.map((row, rowIndex) => {
    if (!Array.isArray(row)) {
      return row;
    }
    const isDataRow = rowIndex > 0;
    return row.map((cell, colIndex) => {
      if (
        isDataRow &&
        (colIndex === INITIAL_RISK_STUDY_SPECIFICS_COLUMN ||
          colIndex === INITIAL_RISK_RISK_SCORE_COLUMN)
      ) {
        return cell;
      }
      return mapParticipantExportCell(cell);
    });
  });
}

export function mapParticipantExportContentForSheet(tableContent, sheetKey) {
  if (sheetKey === "initialRiskAssessment") {
    return mapInitialRiskAssessmentExportContent(tableContent);
  }
  if (PARTICIPANT_EXPORT_SKIP_SHEET_KEYS.has(sheetKey)) {
    return tableContent;
  }
  return mapParticipantExportHeaderRowContent(tableContent);
}

/** Maps string labels in an ExcelJS worksheet (e.g. plan export template headers). */
export function applyParticipantTerminologyToExcelWorksheet(worksheet) {
  if (!worksheet || typeof worksheet.eachRow !== "function") {
    return;
  }
  worksheet.eachRow({ includeEmpty: true }, (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      const value = cell.value;
      if (typeof value === "string") {
        cell.value = toParticipantUiText(value);
      } else if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        value.richText &&
        Array.isArray(value.richText)
      ) {
        cell.value = {
          ...value,
          richText: value.richText.map((part) => ({
            ...part,
            text:
              typeof part.text === "string"
                ? toParticipantUiText(part.text)
                : part.text,
          })),
        };
      }
      if (cell.note) {
        cell.note = mapParticipantExcelCellNote(cell.note);
      }
    });
  });
}
